import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ofType } from "@ngrx/effects";
import { take } from "rxjs";
import { NzSwitchModule } from "ng-zorro-antd/switch";
import { SharedImports } from "../../../components/shared.module";
import { ApplicationDetails } from "../../../core/models/application-details";
import { DataStore } from "../../../core/models/data-store";
import { ApplicationDetailsActions } from "../../../core/ngrx/actions/application-details.actions";
import {
  selectApplicationDetails,
  selectApplicationDetailsLoading,
} from "../../../core/ngrx/selectors/application-details.selectors";
import { BaseLayoutComponent } from "../../base-layout.component";

export interface AssetCapability {
  label: string;
  /** Active green pill when backend reports this capability as configured. */
  configured: boolean;
}

export interface AssetCardView {
  item: any;
  initial: string;
  description: string;
  avatarColor: string;
  capabilities: AssetCapability[];
}

interface AssetUiMeta {
  match: string[];
  description: string;
  avatarColor: string;
  /** Capability labels shown for this asset; configured state comes from backend. */
  capabilityLabels: string[];
}

/** Maps capability label → backend configured flag name. */
const CAPABILITY_CONFIGURED_FLAGS: Record<string, string[]> = {
  LLM: ["llmConfigured"],
  ALM: ["almConfigured"],
  Storage: ["storageConfigured"],
  Parser: ["parserConfigured"],
  DB: ["dbConfigured"],
};

/** UI catalog aligned to the Asset Management Figma / screenshot. */
const ASSET_UI_CATALOG: AssetUiMeta[] = [
  {
    match: ["test data generator", "testdatagenerator", "tdg"],
    description:
      "Creates scenario-specific synthetic test data automatically, ensuring coverage across positive, negative, and boundary conditions without manual effort.",
    avatarColor: "#00434A",
    capabilityLabels: ["LLM", "DB", "Storage"],
  },
  {
    match: ["keyword driven framework", "keyworddriven", "llm driven"],
    description:
      "Simplifies automation by allowing testers to build reusable test cases using business-friendly keywords instead of complex scripts.",
    avatarColor: "#FEC901",
    capabilityLabels: ["LLM"],
  },
  {
    match: ["test execution report", "testExecution"],
    description:
      "Delivers near-real-time test execution visibility with detailed results, execution status, failures, and coverage information.",
    avatarColor: "#FC404A",
    capabilityLabels: ["LLM"],
  },
  {
    match: ["high impacted test cases", "testPrioritizationAndSelection"],
    description:
      "Automatically identifies test cases most affected by application changes and highlights missing or high-priority coverage areas by comparing source code with the test suite.",
    avatarColor: "#C105B3",
    capabilityLabels: ["LLM", "Storage"],
  },
  {
    match: ["test case generator", "testcasegenerator"],
    description:
      "Generates relevant and comprehensive test cases directly from requirements, covering functional, edge, and negative scenarios with clear steps and expected results.",
    avatarColor: "#424751",
    capabilityLabels: ["LLM", "ALM", "Storage", "Parser"],
  },
  {
    match: ["test report summarizer", "testReportSummarizer"],
    description:
      "Converts large volumes of execution results into concise, actionable quality insights, highlighting trends, failures, defects, and overall application health.",
    avatarColor: "#38369A",
    capabilityLabels: ["LLM"],
  },
  {
    match: ["test failure analyzer", "testFailureAnalysis"],
    description:
      "Uses intelligent analysis to determine likely root causes of failures and recommends corrective actions.",
    avatarColor: "#FEC901",
    capabilityLabels: ["LLM"],
  },
  {
    match: ["api automation", "llmapiscript", "llm api"],
    description:
      "Enables automated API testing for functional, integration, regression, and negative scenarios with reusable validations and clear reporting.",
    avatarColor: "#FC404A",
    capabilityLabels: ["LLM"],
  },
  {
    match: ["mobile automation", "llmMobileScriptGenerator"],
    description:
      "Generates and executes automated tests for mobile applications across multiple devices and platforms.",
    avatarColor: "#02F576",
    capabilityLabels: ["LLM"],
  },
  {
    match: ["test script capture", "ai recorder", "airecorder"],
    description:
      "Records application workflows and transforms captured interactions into automation-ready test scripts and frameworks.",
    avatarColor: "#38369A",
    capabilityLabels: ["LLM", "ALM", "Storage", "Parser"],
  },
  {
    match: ["self-healing", "selfHealing"],
    description:
      "Automatically adapts test scripts when UI elements or locators change, reducing test failures caused by application UI modifications.",
    avatarColor: "#C105B3",
    capabilityLabels: ["LLM"],
  },
  {
    match: ["requirement analyzer", "rci"],
    description:
      "AI-driven analysis of user stories and requirements to identify ambiguity, gaps, inconsistencies, and quality issues, with an indicative clarity score and improvement recommendations.",
    avatarColor: "#5f2cff",
    capabilityLabels: ["LLM", "ALM", "Storage","Parser"],
  }

];

/**
 * ManageApplicationsComponent
 *
 * This component is responsible for managing the applications associated with a project.
 * It allows users to view and modify the list of available and selected applications.
 * The component uses Redux store for state management and subscribes to observables
 * to track application details and loading state.
 */
@Component({
  selector: "app-manage-applications",
  standalone: true,
  imports: [FormsModule, CommonModule, ...SharedImports, NzSwitchModule],
  templateUrl: "./manage-applications.component.html",
  styleUrl: "./manage-applications.component.scss",
})
export class ManageApplicationsComponent
  extends BaseLayoutComponent
  implements OnInit
{
  @Input() override dataStore!: DataStore;
  @Output() manageAsset = new EventEmitter<ApplicationDetails>();
  @Output() saved = new EventEmitter<void>();

  loading$ = this.store.select(selectApplicationDetailsLoading);

  isLoading = true;
  availableApplications: ApplicationDetails[] = [];
  selectedApplications: ApplicationDetails[] = [];

  headerLabels = {
    available: "Available",
    selected: "Selected",
  };

  projectId = "";
  projectOverview: any;
  assetsDetails: any[] = [];
  assetCards: AssetCardView[] = [];
  isSaving = false;
  savingAssetKey: string | null = null;

  readonly checkInactiveIcon = "images/virtusa-icons/check-outline-coal.svg";
  readonly checkActiveIcon = "images/virtusa-icons/pass-check.svg";

  ngOnInit(): void {
    this.isSaving = false;
    this.projectService.getProjectOverview().subscribe((item: any) => {
      this.projectOverview = this.extractSurveyAssets(item);
      if (this.hasSurveyAssets()) {
        this.applyAssetList(this.projectOverview);
        this.isLoading = this.isSaving;
      }
    });
    this.loading$.subscribe((loading) => {
      this.isLoading =
        this.isSaving || this.hasSurveyAssets() ? false : !!loading;
    });

    this.projectId = this.dataStore?.data?.id || "";
    if (!this.projectId) {
      this.projectService.getProjectId().subscribe((id) => {
        if (id && !this.projectId) {
          this.projectId = id;
          this.loadPersistedAssets();
        }
      });
    } else {
      this.loadPersistedAssets();
    }

    this.store.select(selectApplicationDetails).subscribe((details) => {
      if (this.isSaving) {
        return;
      }
      // Survey submit returns recommended mappings that are not persisted until Save.
      // Prefer that list so toggles match the questionnaire answers.
      if (this.hasSurveyAssets()) {
        this.applyAssetList(this.projectOverview);
        return;
      }
      const list = Array.isArray(details) ? details : [];
      this.applyAssetList(list);
    });
  }

  private extractSurveyAssets(item: any): ApplicationDetails[] | null {
    if (Array.isArray(item?.data)) {
      return item.data;
    }
    if (Array.isArray(item)) {
      return item;
    }
    return null;
  }

  private hasSurveyAssets(): boolean {
    return Array.isArray(this.projectOverview) && this.projectOverview.length > 0;
  }

  private applyAssetList(list: any[]): void {
    this.assetsDetails = (list || []).map((item: any) => ({ ...item }));
    this.assetCards = this.assetsDetails.map((item) => this.toCardView(item));
  }

  private loadPersistedAssets(): void {
    if (!this.projectId || this.hasSurveyAssets()) {
      return;
    }
    this.store.dispatch(
      ApplicationDetailsActions.loadApplicationDetails({
        projectId: this.projectId,
      })
    );
  }

  onToggleAsset(item: any, enabled: boolean): void {
    if (this.isSaving) {
      item.alreadyMapped = !enabled;
      return;
    }
    item.alreadyMapped = !!enabled;
    this.save(item, !enabled);
  }

  isAssetSaving(item: any): boolean {
    const key = this.assetKey(item);
    return this.isSaving && !!key && key === this.savingAssetKey;
  }

  onManageAsset(item: ApplicationDetails): void {
    this.manageAsset.emit(item);
  }

  /**
   * Generates a payload with updated application mappings and dispatches an action
   * to save the changes to the Redux store.
   */
  save(toggledItem?: any, previousMapped?: boolean) {
    this.isSaving = true;
    this.isLoading = false;
    this.savingAssetKey = this.assetKey(toggledItem);
    this.actions$
      .pipe(
        ofType(
          ApplicationDetailsActions.updateApplicationDetailsSuccess,
          ApplicationDetailsActions.updateApplicationDetailsFailure
        ),
        take(1)
      )
      .subscribe((action) => {
        this.isSaving = false;
        this.savingAssetKey = null;
        if (action.type === ApplicationDetailsActions.updateApplicationDetailsSuccess.type) {
          this.projectService.setProjectOverview(undefined);
          const updated = (
            action as { updatedApplicationDetails?: any[] }
          ).updatedApplicationDetails;
          if (Array.isArray(updated) && updated.length) {
            this.applyAssetList(updated);
          }
          this.saved.emit();
          return;
        }
        if (toggledItem && previousMapped !== undefined) {
          toggledItem.alreadyMapped = previousMapped;
        }
      });
    this.store.dispatch(
      ApplicationDetailsActions.updateApplicationDetails({
        projectId: this.projectId,
        applicationDetails: this.assetsDetails.map((row) => ({ ...row })),
      })
    );
  }

  private assetKey(item: any): string | null {
    const key = item?.id || item?.name;
    return key ? String(key) : null;
  }

  isCapabilityActive(capability: AssetCapability): boolean {
    return !!capability.configured;
  }

  private toCardView(item: any): AssetCardView {
    const meta = this.resolveUiMeta(item);
    const label = String(item?.displayName || item?.name || "").trim();
    return {
      item,
      initial: label ? label.charAt(0).toUpperCase() : "?",
      description: String(
        item?.meta?.description || item?.description || meta.description || ""
      ).trim(),
      avatarColor: item?.meta?.avatarColor || meta.avatarColor,
      capabilities: this.resolveCapabilities(item, meta),
    };
  }

  private resolveUiMeta(item: any): AssetUiMeta {
    const haystack = `${item?.name || ""} ${item?.displayName || ""}`.toLowerCase();
    const found = ASSET_UI_CATALOG.find((entry) =>
      entry.match.some((token) => haystack.includes(token))
    );
    return (
      found || {
        match: [],
        description: "",
        avatarColor: "#D0D2D4",
        capabilityLabels: ["LLM", "ALM", "Storage", "Parser"],
      }
    );
  }

  private resolveCapabilities(item: any, meta: AssetUiMeta): AssetCapability[] {
    const raw =
      item?.meta?.capabilities ||
      item?.capabilities ||
      item?.meta?.tags ||
      item?.meta?.connectionTypes;

    if (Array.isArray(raw) && raw.length) {
      return raw
        .map((value: any) => this.toCapability(value, item))
        .filter(Boolean) as AssetCapability[];
    }

    return meta.capabilityLabels.map((label) => ({
      label,
      configured: this.isConfiguredFromFlags(item, label),
    }));
  }

  private toCapability(value: any, item: any): AssetCapability | null {
    if (typeof value === "string") {
      const label = this.normalizeCapabilityLabel(value);
      if (!label) {
        return null;
      }
      return {
        label,
        configured: this.isConfiguredFromFlags(item, label),
      };
    }

    if (!value || typeof value !== "object") {
      return null;
    }

    const label = this.normalizeCapabilityLabel(
      value.label || value.name || value.type || value.key || ""
    );
    if (!label) {
      return null;
    }

    const configured =
      value.configured === true ||
      value.isConfigured === true ||
      value.active === true ||
      this.isConfiguredFromFlags(item, label);

    return { label, configured };
  }

  private isConfiguredFromFlags(item: any, label: string): boolean {
    const sources = [item, item?.meta].filter(Boolean);
    const flagKeys =
      CAPABILITY_CONFIGURED_FLAGS[label] ||
      CAPABILITY_CONFIGURED_FLAGS[label.toUpperCase()] ||
      [`is${label}Configured`, `${label.toLowerCase()}Configured`];

    return sources.some((source) =>
      flagKeys.some((key) => source?.[key] === true)
    );
  }

  private normalizeCapabilityLabel(value: string): string {
    const key = String(value || "").trim();
    if (!key) {
      return "";
    }
    const normalized = key.toLowerCase().replace(/[\s_-]+/g, "");
    const known: Record<string, string> = {
      llm: "LLM",
      alm: "ALM",
      storage: "Storage",
      file: "Storage",
      parser: "Parser",
      llmparser: "Parser",
      db: "DB",
    };
    return known[normalized] || key;
  }
}
